import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import PeriodStatusButtonGroupAtom, {
  PeriodStatusButtonGroupOption,
} from '../../../atoms/PeriodStatusButtonGroup/PeriodStatusButtonGroupAtom';
import ApplyToAllShortcutMolecule, {
  ApplyToAllShortcutOption,
} from '../ApplyToAllShortcut/ApplyToAllShortcutMolecule';
import './CatchUpStripMolecule.scss';

export interface CatchUpPeriod {
  periodId: string;
  /** Preferred display label (e.g. "Q3 2025"). Falls back to the formatted `dueAt`. */
  label?: string;
  dueAt: string;
}

export interface CatchUpStripStatusOption
  extends PeriodStatusButtonGroupOption,
    ApplyToAllShortcutOption {}

export interface CatchUpStripSubmitPayload {
  statuses: Array<{ periodId: string; statusValue: string }>;
  comment?: string;
}

export interface CatchUpStripMoleculeProps {
  /** Periods to catch up, in chronological order (oldest → newest). */
  periods: CatchUpPeriod[];
  /** Status options used both in the per-period grid and the apply-to-all row. */
  statuses: CatchUpStripStatusOption[];
  /** Initial per-period statuses keyed by `periodId`. Defaults to newest=on_track, older=skipped. */
  initialStatuses?: Record<string, string>;
  /** Locale override for the period due-date fallback formatting. */
  locale?: string;
  /** Disables submit + cancel and renders a small spinner in the footer. */
  loading?: boolean;
  /** Cancel button handler. */
  onCancel: () => void;
  /** Submit handler — receives the per-period statuses + optional comment. */
  onSubmit: (payload: CatchUpStripSubmitPayload) => void;
  className?: string;
}

const DEFAULT_NEWEST_STATUS = 'on_track';
const DEFAULT_OLDER_STATUS = 'skipped';

function formatDueDate(iso: string, locale?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function buildInitialStatuses(
  periods: CatchUpPeriod[],
  initial?: Record<string, string>
): Record<string, string> {
  const result: Record<string, string> = {};
  if (periods.length === 0) return result;
  const newestIndex = periods.length - 1;
  periods.forEach((period, index) => {
    const supplied = initial?.[period.periodId];
    if (supplied) {
      result[period.periodId] = supplied;
      return;
    }
    result[period.periodId] =
      index === newestIndex ? DEFAULT_NEWEST_STATUS : DEFAULT_OLDER_STATUS;
  });
  return result;
}

const CatchUpStripMolecule: React.FC<CatchUpStripMoleculeProps> = ({
  periods,
  statuses,
  initialStatuses,
  locale,
  loading = false,
  onCancel,
  onSubmit,
  className = '',
}) => {
  const { t } = useTranslation();

  const [statusByPeriod, setStatusByPeriod] = useState<Record<string, string>>(
    () => buildInitialStatuses(periods, initialStatuses)
  );
  const [comment, setComment] = useState<string>('');

  const handleApplyToAll = useCallback(
    (statusValue: string) => {
      setStatusByPeriod((prev) => {
        const next: Record<string, string> = {};
        Object.keys(prev).forEach((periodId) => {
          next[periodId] = statusValue;
        });
        // Ensure any newly arrived periods also get the value.
        periods.forEach((period) => {
          next[period.periodId] = statusValue;
        });
        return next;
      });
    },
    [periods]
  );

  const handlePeriodChange = useCallback(
    (periodId: string, statusValue: string) => {
      setStatusByPeriod((prev) => ({ ...prev, [periodId]: statusValue }));
    },
    []
  );

  const handleSubmit = useCallback(() => {
    if (loading || periods.length === 0) return;
    const payload: CatchUpStripSubmitPayload = {
      statuses: periods.map((period) => ({
        periodId: period.periodId,
        statusValue: statusByPeriod[period.periodId],
      })),
    };
    if (comment.trim().length > 0) {
      payload.comment = comment;
    }
    onSubmit(payload);
  }, [loading, periods, statusByPeriod, comment, onSubmit]);

  const summary = useMemo(() => {
    if (periods.length === 0) return '';
    const counts = new Map<string, number>();
    periods.forEach((period) => {
      const statusValue = statusByPeriod[period.periodId];
      if (!statusValue) return;
      counts.set(statusValue, (counts.get(statusValue) ?? 0) + 1);
    });
    const labelByValue = new Map<string, string>(
      statuses.map((status) => [status.value, status.label])
    );
    const separator = t('taskList.catchUpStrip.summarySeparator');
    return Array.from(counts.entries())
      .map(([value, count]) =>
        t('taskList.catchUpStrip.summaryItem', {
          count,
          label: (labelByValue.get(value) ?? value).toLowerCase(),
        })
      )
      .join(separator);
  }, [periods, statusByPeriod, statuses, t]);

  const ariaSetStatusPrefix = t('taskList.catchUpStrip.ariaSetStatus');

  const classes = ['catch-up-strip-molecule', className]
    .filter(Boolean)
    .join(' ');

  if (periods.length === 0) {
    return (
      <section className={classes} aria-label={t('taskList.catchUpStrip.noPeriods')}>
        <p className="catch-up-strip-molecule__empty">
          {t('taskList.catchUpStrip.noPeriods')}
        </p>
      </section>
    );
  }

  const titleText = t('taskList.catchUpStrip.title', { count: periods.length });
  const submitText = t('taskList.catchUpStrip.submit', { count: periods.length });

  return (
    <section className={classes} aria-label={titleText}>
      <header className="catch-up-strip-molecule__header">
        <div className="catch-up-strip-molecule__heading">
          <i
            className="catch-up-strip-molecule__heading-icon pi pi-copy"
            aria-hidden="true"
          />
          <div className="catch-up-strip-molecule__heading-text">
            <h4 className="catch-up-strip-molecule__title">{titleText}</h4>
            <p className="catch-up-strip-molecule__hint">
              {t('taskList.catchUpStrip.hint')}
            </p>
          </div>
        </div>
        <ApplyToAllShortcutMolecule
          statuses={statuses}
          onApplyToAll={handleApplyToAll}
          disabled={loading}
          className="catch-up-strip-molecule__apply-to-all"
        />
      </header>

      <ul className="catch-up-strip-molecule__period-grid">
        {periods.map((period) => {
          const periodLabel =
            period.label && period.label.length > 0
              ? period.label
              : formatDueDate(period.dueAt, locale);
          return (
            <li
              key={period.periodId}
              className="catch-up-strip-molecule__period-card"
            >
              <div className="catch-up-strip-molecule__period-header">
                <span className="catch-up-strip-molecule__period-label">
                  {periodLabel}
                </span>
                <span className="catch-up-strip-molecule__period-due">
                  {formatDueDate(period.dueAt, locale)}
                </span>
              </div>
              <PeriodStatusButtonGroupAtom
                statuses={statuses}
                value={statusByPeriod[period.periodId] ?? null}
                onChange={(statusValue) =>
                  handlePeriodChange(period.periodId, statusValue)
                }
                ariaLabelPrefix={ariaSetStatusPrefix}
                disabled={loading}
                className="catch-up-strip-molecule__period-buttons"
              />
            </li>
          );
        })}
      </ul>

      <div className="catch-up-strip-molecule__notes">
        <textarea
          className="catch-up-strip-molecule__notes-input"
          rows={2}
          placeholder={t('taskList.catchUpStrip.notesPlaceholder')}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          disabled={loading}
        />
      </div>

      <footer className="catch-up-strip-molecule__footer">
        <div className="catch-up-strip-molecule__summary">
          {loading ? (
            <span className="catch-up-strip-molecule__submitting">
              <i
                className="pi pi-spin pi-spinner catch-up-strip-molecule__spinner"
                aria-hidden="true"
              />
              {t('taskList.catchUpStrip.submitting')}
            </span>
          ) : (
            summary
          )}
        </div>
        <div className="catch-up-strip-molecule__actions">
          <button
            type="button"
            className="Save-button"
            onClick={handleSubmit}
            disabled={loading}
          >
            <i
              className="pi pi-check catch-up-strip-molecule__submit-icon mr-2"
              aria-hidden="true"
            />
            {submitText}
          </button>
          <button
            type="button"
            className="Close-button"
            onClick={onCancel}
            disabled={loading}
          >
            {t('taskList.catchUpStrip.cancel')}
          </button>
        </div>
      </footer>
    </section>
  );
};

CatchUpStripMolecule.displayName = 'CatchUpStripMolecule';

export default CatchUpStripMolecule;
